import React, { useCallback, useEffect, useState } from 'react';

import PersonFormActionButtons from '../../../../components/pagesComponents/vidaPageComponents/PersonFormActionButtons';
import SeguradoInconsistencyModal from '../../../../components/pagesComponents/vidaPageComponents/modal/SeguradoInconsistencyModal';
import { usePersonFormWizard } from '../../../../hooks/vida/usePersonFormWizard';
import { useSeguradoRelatedPanels } from '../../../../hooks/vida/useSeguradoRelatedPanels';
import type { NewInsuredFormValues } from '../../../../types/vida/cadastralMaintenance/newInsuredFormTypes';
import type { PersonFormSection } from '../../../../types/vida/cadastralMaintenance/personFormTypes';
import type {
  SeguradoDataReturn,
  SeguradoDetailBeneficiaryItem,
} from '../../../../types/vida/maintenanceSegurado/maintenanceSeguradoType';
import {
  isSeguradoInconsistencyError,
  parseSeguradoInconsistenciesFromError,
} from '../../../../utils/selfBillingUtil';
import SeguradoFormSection from './SeguradoFormSection';
import SeguradoRelatedPanelsSection from './SeguradoRelatedPanelsSection';

type NewSeguradoFormProps = {
  isSaving: boolean;
  formKey: number;
  onSave: (
    values: NewInsuredFormValues,
    conjugueValues?: NewInsuredFormValues | null,
    beneficiarios?: SeguradoDetailBeneficiaryItem[],
  ) => Promise<void>;
  onCancel: () => void;
};

const NewSeguradoForm: React.FC<NewSeguradoFormProps> = ({
  isSaving,
  formKey,
  onSave,
  onCancel,
}) => {
  const [isValid, setIsValid] = useState(false);
  const [draftValues, setDraftValues] = useState<NewInsuredFormValues | null>(null);
  const [isInconsistencyModalOpen, setIsInconsistencyModalOpen] = useState(false);
  const [inconsistencies, setInconsistencies] = useState<SeguradoDataReturn[]>([]);
  const [beneficiarios, setBeneficiarios] = useState<SeguradoDetailBeneficiaryItem[]>([]);
  const [formSection, setFormSection] = useState<PersonFormSection>('personData');

  const {
    sectionControlRef,
    onActiveSectionChange: onWizardSectionChange,
  } = usePersonFormWizard(formKey);

  const relatedPanels = useSeguradoRelatedPanels({
    formKey,
    beneficiarios,
    setBeneficiarios,
    formSection,
    preserveDrafts: true,
    showConjugueStep: true,
  });

  const onActiveSectionChange = useCallback((section: PersonFormSection) => {
    setFormSection(section);
    onWizardSectionChange(section);
  }, [onWizardSectionChange]);

  const { resetAllRelatedPanels } = relatedPanels;

  useEffect(() => {
    setDraftValues(null);
    setIsValid(false);
    setIsInconsistencyModalOpen(false);
    setInconsistencies([]);
    setBeneficiarios([]);
    resetAllRelatedPanels();
    setFormSection('personData');
  }, [formKey, resetAllRelatedPanels]);

  const handleSave = async () => {
    if (!draftValues) {
      return;
    }

    try {
      await onSave(draftValues, relatedPanels.conjugueDraftValues, beneficiarios);
    } catch (error) {
      const parsedInconsistencies = isSeguradoInconsistencyError(error)
        ? error.inconsistencies
        : parseSeguradoInconsistenciesFromError(error);

      if (parsedInconsistencies.length > 0) {
        setInconsistencies(parsedInconsistencies);
        setIsInconsistencyModalOpen(true);
      }
    }
  };

  const handleCloseInconsistencyModal = () => {
    setIsInconsistencyModalOpen(false);
    setInconsistencies([]);
  };

  const handleWizardContinueOrSave = () => {
    relatedPanels.handleRelatedPanelsContinue({
      onSave: handleSave,
      goToNextFormSection: () => sectionControlRef.current?.goToNextSection(),
    });
  };

  const handleCancel = () => {
    relatedPanels.handleRelatedPanelsBack({
      onExit: onCancel,
      clearDraftOnBack: false,
    });
  };

  const isOnRelatedPanel = relatedPanels.isConjugueFormActive
    || relatedPanels.isConjuguePanelActive
    || relatedPanels.isBeneficiariosListActive
    || relatedPanels.isBeneficiariosDetailActive;

  return (
    <>
      <div className="bsds-mt-24" role="region" aria-label="Cadastro de novo segurado" aria-busy={isSaving}>
        <SeguradoFormSection
          key={`new-segurado-${formKey}`}
          readOnly={isSaving}
          showDataCadastro
          onValidityChange={setIsValid}
          onValuesChange={setDraftValues}
          showBeneficiariosButton
          isBeneficiariosActive={
            relatedPanels.isBeneficiariosListActive || relatedPanels.isBeneficiariosDetailActive
          }
          onBeneficiariosClick={relatedPanels.handleOpenBeneficiarios}
          showConjugueButton
          isConjugueActive={relatedPanels.isConjugueScreenActive}
          onConjugueClick={relatedPanels.handleOpenConjugue}
          onExitExtraPanels={relatedPanels.handleExitExtraPanels}
          sectionControlRef={sectionControlRef}
          onActiveSectionChange={onActiveSectionChange}
        />

        <SeguradoRelatedPanelsSection
          formKey={formKey}
          isSaving={isSaving}
          isBeneficiariosListActive={relatedPanels.isBeneficiariosListActive}
          isBeneficiariosDetailActive={relatedPanels.isBeneficiariosDetailActive}
          isConjugueScreenActive={relatedPanels.isConjugueScreenActive}
          isConjugueFormActive={relatedPanels.isConjugueFormActive}
          beneficiariosRows={relatedPanels.beneficiariosRows}
          selectedBeneficiario={relatedPanels.selectedBeneficiario}
          beneficiarioDraft={relatedPanels.beneficiarioDraft}
          beneficiarioFormInitialValues={relatedPanels.beneficiarioFormInitialValues}
          conjugueDisplayValues={relatedPanels.conjugueDisplayValues}
          conjugueSectionControlRef={relatedPanels.conjugueSectionControlRef}
          onConjugueActiveSectionChange={relatedPanels.onConjugueActiveSectionChange}
          onEditBeneficiario={relatedPanels.handleEditBeneficiario}
          onAddBeneficiario={relatedPanels.handleAddBeneficiario}
          onBeneficiarioValidityChange={relatedPanels.setIsBeneficiarioValid}
          onBeneficiarioValuesChange={relatedPanels.setBeneficiarioDraft}
          onAddConjugue={relatedPanels.handleAddConjugue}
          onEditConjugue={relatedPanels.handleEditConjugue}
          onConjugueValidityChange={relatedPanels.setIsConjugueFormValid}
          onConjugueValuesChange={relatedPanels.setConjugueDraftValues}
        />

        <PersonFormActionButtons
          isLastSection={
            relatedPanels.isConjugueFormActive
              ? relatedPanels.isConjugueLastSection
              : relatedPanels.isWizardLastSection
          }
          isSaving={isSaving}
          canSave={
            relatedPanels.isConjugueFormActive
              ? relatedPanels.isConjugueFormValid
              : isValid && draftValues != null
          }
          onPrimaryClick={handleWizardContinueOrSave}
          onCancel={handleCancel}
          saveLabel={relatedPanels.isConjugueFormActive ? 'Confirmar Cônjuge' : 'Salvar'}
          saveAriaLabel={
            relatedPanels.isConjugueFormActive
              ? 'Confirmar dados do cônjuge'
              : 'Salvar cadastro de segurado'
          }
          continueAriaLabel={
            relatedPanels.isConjugueFormActive
              ? 'Continuar cadastro do cônjuge'
              : 'Continuar cadastro de segurado'
          }
          cancelAriaLabel={isOnRelatedPanel ? 'Voltar' : 'Cancelar cadastro de segurado'}
          cancelLabel={isOnRelatedPanel ? 'Voltar' : 'Cancelar'}
        />
      </div>

      <SeguradoInconsistencyModal
        isOpen={isInconsistencyModalOpen}
        inconsistencies={inconsistencies}
        onClose={handleCloseInconsistencyModal}
      />
    </>
  );
};

export default NewSeguradoForm;
